import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from './contexts/AuthContext';

// Layout Components — Always eagerly loaded (needed on every page)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FloatingContactButton from './components/FloatingContactButton';
import ScrollTop from './components/ScrollTop';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { OnlineUsersProvider } from './contexts/OnlineUsersContext';

// ─── Lazy-loaded page components ────────────────────────────────────────────
// Auth Pages
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));

// User Pages
const Profile = lazy(() => import('./pages/user/Profile'));
const UserProfile = lazy(() => import('./pages/user/UserProfile'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Billing = lazy(() => import('./pages/Billing'));
const UserDashboard = lazy(() => import('./pages/user/UserDashboard'));
const Saved = lazy(() => import('./pages/user/Saved'));
const Followers = lazy(() => import('./pages/user/Followers'));
const Following = lazy(() => import('./pages/user/Following'));
const Connections = lazy(() => import('./pages/user/Connections'));
const AccountDeletion = lazy(() => import('./pages/user/AccountDeletion'));
const ProjectPage = lazy(() => import('./pages/user/ProjectPage'));

// Event Pages
const Events = lazy(() => import('./pages/events/Events'));
const EventDetail = lazy(() => import('./pages/events/EventDetail'));
const CreateEvent = lazy(() => import('./pages/events/CreateEvent'));
const EditEvent = lazy(() => import('./pages/events/EditEvent'));
const EventCertificateBuilder = lazy(() => import('./pages/events/EventCertificateBuilder'));

// Job Pages
const Jobs = lazy(() => import('./pages/jobs/Jobs'));
const JobDetail = lazy(() => import('./pages/jobs/JobDetail'));

// Hackathon Pages
const Hackathons = lazy(() => import('./pages/hackathons/Hackathons'));
const HackathonDetail = lazy(() => import('./pages/hackathons/HackathonDetail'));
const EditHackathonWizard = lazy(() => import('./pages/hackathons/EditHackathonWizard'));
const HackathonTeamPage = lazy(() => import('./pages/hackathons/HackathonTeamPage'));
const HackathonAnnouncements = lazy(() => import('./pages/hackathons/HackathonAnnouncements'));
const HackathonTeamsViewPage = lazy(() => import('./pages/hackathons/HackathonTeamsViewPage'));
const SubmissionsPage = lazy(() => import('./pages/hackathons/SubmissionsPage'));
const HackathonApprovedTeamsPage = lazy(() => import('./pages/hackathons/HackathonApprovedTeamsPage'));
const HackathonCertificateBuilder = lazy(() => import('./pages/hackathons/HackathonCertificateBuilder'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));

// Quiz Pages
const Quizzes = lazy(() => import('./pages/quizzes/Quizzes'));
const QuizDetail = lazy(() => import('./pages/quizzes/QuizDetail'));
const CreateQuiz = lazy(() => import('./pages/quizzes/CreateQuiz'));
const EditQuiz = lazy(() => import('./pages/quizzes/EditQuiz'));
const TakeQuiz = lazy(() => import('./pages/quizzes/TakeQuiz'));
const Leaderboard = lazy(() => import('./pages/quizzes/Leaderboard'));

// Admin Pages
const EventsAdminPage = lazy(() => import('./pages/admin/EventsAdminPage'));
const QuizParticipantsPage = lazy(() => import('./pages/admin/QuizParticipantsPage'));
const HackathonAnnouncementsPage = lazy(() => import('./pages/admin/HackathonAnnouncementsPage'));

// Other Pages
const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ReferralRedirect = lazy(() => import('./pages/ReferralRedirect'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsConditionsPage = lazy(() => import('./pages/TermsConditionsPage'));
const PartnerWithUsPage = lazy(() => import('./pages/PartnerWithUs'));
const OrganiserPage = lazy(() => import('./pages/OrganiserPage'));
const TeamSearchPage = lazy(() => import('./pages/TeamSearchPage'));
const TeamDetailPage = lazy(() => import('./pages/TeamDetailPage'));
const MyTeamPage = lazy(() => import('./pages/MyTeamPage'));

// ─── Global Page Loading Fallback ────────────────────────────────────────────
const PageLoader: React.FC = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      <p className="text-sm text-slate-400 animate-pulse">Loading...</p>
    </div>
  </div>
);

// ─── React Query Client ────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,        // 30 seconds — avoids refetching on quick nav
      cacheTime: 5 * 60 * 1000,    // 5 minutes — keep data in memory
    },
  },
});

// ─── ProtectedRoute ─────────────────────────────────────────────────────────
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return children;
};

// ─── App Content ─────────────────────────────────────────────────────────────
const AppContent: React.FC = () => {
  const location = useLocation();
  const isMessagesPage = location.pathname === '/messages';

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        <ScrollTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsConditionsPage />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/partner-with-us" element={<PartnerWithUsPage />} />
            <Route path="/organiser" element={<OrganiserPage />} />
            <Route path="/hackathons/:username/team" element={<ProtectedRoute><HackathonTeamPage /></ProtectedRoute>} />
            <Route path="/saved" element={<ProtectedRoute><Saved /></ProtectedRoute>} />
            <Route path="/create-hackathon" element={<ProtectedRoute><EditHackathonWizard /></ProtectedRoute>} />
            <Route path="/create-event" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
            <Route path="/hackathons/:username/edit" element={<ProtectedRoute><EditHackathonWizard /></ProtectedRoute>} />
            <Route path="/events/:username/edit" element={<ProtectedRoute><EditEvent /></ProtectedRoute>} />
            <Route path="/create-quiz" element={<ProtectedRoute><CreateQuiz /></ProtectedRoute>} />
            <Route path="/quizzes" element={<Quizzes />} />
            <Route path="/quiz/:id" element={<ProtectedRoute><QuizDetail /></ProtectedRoute>} />
            <Route path="/quiz/:id/take" element={<ProtectedRoute><TakeQuiz /></ProtectedRoute>} />
            <Route path="/quiz/:id/preview" element={<ProtectedRoute><TakeQuiz /></ProtectedRoute>} />
            <Route path="/quiz/:id/edit" element={<ProtectedRoute><EditQuiz /></ProtectedRoute>} />
            <Route path="/quizzes/:id/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/hackathons" element={<Hackathons />} />
            <Route path="/hackathons/:username" element={<ProtectedRoute><HackathonDetail /></ProtectedRoute>} />
            <Route path="/hackathons/:username/submissions" element={<ProtectedRoute><SubmissionsPage /></ProtectedRoute>} />
            <Route path="/hackathons/:username/approved-teams" element={<ProtectedRoute><HackathonApprovedTeamsPage /></ProtectedRoute>} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:username" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
            {/* Subscription & Billing */}
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/user-dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
            <Route path="/admin/hackathons/:username/announcements" element={<ProtectedRoute><HackathonAnnouncementsPage /></ProtectedRoute>} />
            <Route path="/hackathons/:username/manage-teams" element={<ProtectedRoute><HackathonTeamsViewPage /></ProtectedRoute>} />
            <Route path="/admin/events/:eventId/participants" element={<ProtectedRoute><EventsAdminPage /></ProtectedRoute>} />
            <Route path="/admin/quizzes/:quizId/participants" element={<ProtectedRoute><QuizParticipantsPage /></ProtectedRoute>} />
            <Route path="/hackathons/:username/announcements" element={<ProtectedRoute><HackathonAnnouncements /></ProtectedRoute>} />
            <Route path="/in/:username" element={<UserProfile />} />
            <Route path="/project/:username/:projectId" element={<ProjectPage />} />
            <Route path="/user/:username/followers" element={<ProtectedRoute><Followers /></ProtectedRoute>} />
            <Route path="/user/:username/following" element={<ProtectedRoute><Following /></ProtectedRoute>} />
            <Route path="/user/:username/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
            <Route path="/delete-account" element={<ProtectedRoute><AccountDeletion /></ProtectedRoute>} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/search-teams" element={<TeamSearchPage />} />
            <Route path="/myteam" element={<ProtectedRoute><MyTeamPage /></ProtectedRoute>} />
            <Route path="/teams/:teamId" element={<ProtectedRoute><TeamDetailPage /></ProtectedRoute>} />
            <Route path="/hackathons/:username/certificate" element={<ProtectedRoute><HackathonCertificateBuilder /></ProtectedRoute>} />
            <Route path="/events/:username/certificate" element={<ProtectedRoute><EventCertificateBuilder /></ProtectedRoute>} />
            <Route path="/verify/certificate/:id" element={<VerifyCertificate />} />
            <Route path="/ref/:code" element={<ReferralRedirect />} />
          </Routes>
        </Suspense>
      </main>
      {!isMessagesPage && <Footer />}
      <FloatingContactButton />
    </div>
  );
};

// ─── Root App ─────────────────────────────────────────────────────────────────
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SocketProvider>
          <OnlineUsersProvider>
            <Router>
              <AppContent />
            </Router>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10B981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#EF4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </OnlineUsersProvider>
        </SocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;